import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { api, getErrorMessage } from "@/lib/api";

/** One complete call per code+state, shared if this page mounts twice. */
const completeInflight = new Map();

function callbackParams() {
  let search = window.location.search;
  if (!new URLSearchParams(search).get("code")) {
    try {
      const stashed = sessionStorage.getItem("paisacx.truidCallback") || "";
      if (stashed.includes("code=")) search = stashed;
    } catch {
      /* storage unavailable */
    }
  }
  const q = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  return {
    code: q.get("code") || "",
    codeVerifier: q.get("codeVerifier") || q.get("code_verifier") || "",
    state: q.get("state") || "",
  };
}

function completeDigilocker(code, codeVerifier, state) {
  const key = `${state}:${code}`;
  let pending = completeInflight.get(key);
  if (!pending) {
    pending = api
      .post(
        "/api/kyc/truid/digilocker/complete",
        { code, code_verifier: codeVerifier, state },
        { timeout: 60000 },
      )
      .finally(() => {
        completeInflight.delete(key);
      });
    completeInflight.set(key, pending);
  }
  return pending;
}

/**
 * TruID DigiLocker redirect landing.
 * Callback URL receives ?code=&state= (sandbox omits codeVerifier).
 */
export default function TruidKycCallback() {
  const navigate = useNavigate();
  const [message, setMessage] = useState("Finishing DigiLocker KYC…");
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;

    (async () => {
      const { code, codeVerifier, state } = callbackParams();
      if (!code || !state) {
        if (!alive) return;
        setErr("Missing DigiLocker callback parameters (code / state).");
        setMessage("Could not complete DigiLocker KYC.");
        return;
      }

      try {
        const { data } = await completeDigilocker(code, codeVerifier, state);
        try {
          sessionStorage.removeItem("paisacx.truidCallback");
        } catch {
          /* ignore */
        }
        if (!alive) return;

        if (data?.next_step === "pan" || data?.status === "awaiting_pan") {
          setMessage("Aadhaar fetched. Continue with PAN verification…");
        } else if (data?.next_step === "selfie" || data?.status === "awaiting_selfie") {
          setMessage("DigiLocker done. Continue with face match…");
        } else if (data?.status === "approved") {
          setMessage("KYC approved. Redirecting…");
        } else {
          setMessage("Status updated. Redirecting…");
        }

        if (window.opener && !window.opener.closed) {
          try {
            window.opener.postMessage(
              { type: "truid-digilocker-complete", status: data?.status },
              window.location.origin,
            );
          } catch {
            /* ignore */
          }
        }
        window.setTimeout(() => {
          if (alive) navigate("/profile/kyc", { replace: true });
        }, 600);
      } catch (e) {
        if (!alive) return;
        setErr(getErrorMessage(e));
        setMessage("Could not complete DigiLocker KYC.");
      }
    })();

    return () => {
      alive = false;
    };
  }, [navigate]);

  return (
    <div className="min-h-[60vh] grid place-items-center px-6">
      <div className="text-center max-w-md space-y-3">
        <Loader2 className="animate-spin mx-auto text-primary" size={28} />
        <p className="text-[15px] font-medium text-foreground">{message}</p>
        {err ? <p className="text-[13px] text-down">{err}</p> : null}
        <Link to="/profile/kyc" className="text-[13px] text-primary underline">
          Back to KYC
        </Link>
      </div>
    </div>
  );
}
